import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { motion } from "framer-motion";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { GraduationCap, Send } from "lucide-react";

const formSchema = z.object({
  studentName: z.string().min(2, "Student name is required"),
  parentName: z.string().min(2, "Parent name is required"),
  classApplying: z.string().min(1, "Please select a class"),
  dateOfBirth: z.string().min(1, "Date of birth is required"),
  location: z.string().min(2, "Location is required"),
  mobile: z.string().min(10, "Valid mobile number required"),
  email: z.string().email("Valid email is required"),
  previousSchool: z.string().optional(),
  message: z.string().optional(),
});

const classOptions = [
  "Pre-Nursery",
  "Nursery",
  "LKG",
  "UKG",
  "Class 1",
  "Class 2",
  "Class 3",
  "Class 4",
  "Class 5",
  "Class 6",
  "Class 7",
  "Class 8",
  "Class 9",
  "Class 10",
];

export const AdmissionFormInline = () => {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      studentName: "",
      parentName: "",
      classApplying: "",
      dateOfBirth: "",
      location: "",
      mobile: "",
      email: "",
      previousSchool: "",
      message: "",
    },
  });

  const onSubmit = async (data) => {
    try {
      // Store in localStorage for demo
      const existingEnquiries = JSON.parse(localStorage.getItem("admissionEnquiries") || "[]");
      const newEnquiry = {
        ...data,
        id: Date.now(),
        submittedAt: new Date().toISOString(),
      };
      localStorage.setItem("admissionEnquiries", JSON.stringify([...existingEnquiries, newEnquiry]));
      
      toast.success("Application submitted successfully!", {
        description: "We will contact you soon regarding the admission process.",
      });
      reset();
    } catch (error) {
      toast.error("Failed to submit application. Please try again.");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.6, delay: 0.3 }}
      className="bg-white rounded-2xl shadow-2xl p-6 lg:p-8 admission-form-card"
      data-testid="inline-admission-form"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
          <GraduationCap className="text-white" size={24} />
        </div>
        <div>
          <h3 className="font-heading text-xl font-bold text-gray-900">Apply for Admission</h3>
          <p className="text-sm text-muted-foreground">Session 2024-25</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="studentName" className="text-sm">Student Name *</Label>
            <Input
              id="studentName"
              placeholder="Student name"
              {...register("studentName")}
              className={`h-10 ${errors.studentName ? "border-red-500" : ""}`}
              data-testid="inline-student-name"
            />
            {errors.studentName && (
              <p className="text-xs text-red-500">{errors.studentName.message}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="parentName" className="text-sm">Parent Name *</Label>
            <Input
              id="parentName"
              placeholder="Parent name"
              {...register("parentName")}
              className={`h-10 ${errors.parentName ? "border-red-500" : ""}`}
              data-testid="inline-parent-name"
            />
            {errors.parentName && (
              <p className="text-xs text-red-500">{errors.parentName.message}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="classApplying" className="text-sm">Class *</Label>
            <Select onValueChange={(value) => setValue("classApplying", value)}>
              <SelectTrigger className={`h-10 ${errors.classApplying ? "border-red-500" : ""}`} data-testid="inline-class">
                <SelectValue placeholder="Select class" />
              </SelectTrigger>
              <SelectContent>
                {classOptions.map((cls) => (
                  <SelectItem key={cls} value={cls}>
                    {cls}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.classApplying && (
              <p className="text-xs text-red-500">{errors.classApplying.message}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="dateOfBirth" className="text-sm">Date of Birth *</Label>
            <Input
              id="dateOfBirth"
              type="date"
              {...register("dateOfBirth")}
              className={`h-10 ${errors.dateOfBirth ? "border-red-500" : ""}`}
              data-testid="inline-dob"
            />
            {errors.dateOfBirth && (
              <p className="text-xs text-red-500">{errors.dateOfBirth.message}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="mobile" className="text-sm">Mobile *</Label>
            <Input
              id="mobile"
              type="tel"
              placeholder="Mobile number"
              {...register("mobile")}
              className={`h-10 ${errors.mobile ? "border-red-500" : ""}`}
              data-testid="inline-mobile"
            />
            {errors.mobile && (
              <p className="text-xs text-red-500">{errors.mobile.message}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-sm">Email *</Label>
            <Input
              id="email"
              type="email"
              placeholder="Email address"
              {...register("email")}
              className={`h-10 ${errors.email ? "border-red-500" : ""}`}
              data-testid="inline-email"
            />
            {errors.email && (
              <p className="text-xs text-red-500">{errors.email.message}</p>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="location" className="text-sm">City / Location *</Label>
          <Input
            id="location"
            placeholder="Your city"
            {...register("location")}
            className={`h-10 ${errors.location ? "border-red-500" : ""}`}
            data-testid="inline-location"
          />
          {errors.location && (
            <p className="text-xs text-red-500">{errors.location.message}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="previousSchool" className="text-sm">Previous School</Label>
          <Input
            id="previousSchool"
            placeholder="Previous school (if any)"
            {...register("previousSchool")}
            className="h-10"
            data-testid="inline-previous-school"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="message" className="text-sm">Message</Label>
          <Textarea
            id="message"
            placeholder="Any additional notes..."
            rows={2}
            {...register("message")}
            data-testid="inline-message"
          />
        </div>

        <Button
          type="submit"
          className="w-full bg-secondary hover:bg-secondary/90 text-white rounded-full h-12 text-base font-semibold"
          disabled={isSubmitting}
          data-testid="inline-submit-btn"
        >
          {isSubmitting ? (
            "Submitting..."
          ) : (
            <>
              <Send size={18} className="mr-2" />
              Submit Application
            </>
          )}
        </Button>
      </form>
    </motion.div>
  );
};
